import React from 'react';
import { Shield, Lock, Eye, Server } from 'lucide-react';

const EncryptedEmailSection = () => {
  const encryptionFeatures = [
    {
      icon: Shield,
      title: 'Zero-access encryption',
      description: 'Your emails are encrypted before leaving your device. We cannot access your data even if compelled.',
    },
    {
      icon: Lock,
      title: 'End-to-end protection',
      description: 'Messages remain encrypted throughout their entire journey, from sender to recipient.',
    },
    {
      icon: Eye,
      title: 'No tracking or profiling',
      description: 'We never scan your emails for advertising or build profiles. Your privacy is absolute.',
    },
    {
      icon: Server,
      title: 'Secure infrastructure',
      description: 'Data centers located in privacy-focused jurisdictions with military-grade physical security.',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-900 text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Enterprise-grade email encryption
          </h2>
          <p className="text-xl text-indigo-200 max-w-3xl mx-auto">
            Guarded Mail Box implements multiple layers of encryption and security protocols to ensure your communications remain completely private and secure.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {encryptionFeatures.map((feature, index) => (
            <div
              key={index}
              className="group relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 h-full border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <div className="mb-6">
                  <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center group-hover:rotate-6 transition-transform duration-300">
                    <feature.icon className="h-7 w-7 text-white" strokeWidth={2.5} />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-3">
                  {feature.title}
                </h3>
                
                <p className="text-indigo-200 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-8 py-4">
            <Lock className="h-6 w-6 text-amber-400" />
            <span className="text-lg font-semibold">AES-256 Encryption Standard</span>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ animationDuration: '5s' }}></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ animationDuration: '7s' }}></div>
    </section>
  );
};

export default EncryptedEmailSection;